import { yupResolver } from '@hookform/resolvers/yup';
import { MutationFunction, useMutation } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { ObjectSchema } from 'yup';
import { useSession } from '@/auth/index';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import { SearchData, openReportInNewTab } from '@/reports/index';
import { SearchType } from '@/reports/openReport/openReport';
import {
  FormControl,
  Input,
  Stack,
  Text,
  Icons,
  InputAdornment,
  IconButton,
  styled,
} from '@/theme/index';
import { searchFormsRecyclingValidation } from '@/utils/index';

const SquareIconButton = styled(IconButton)(({ theme }) => ({
  borderRadius: '6px',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  height: '40px',
  width: '40px',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const ClearIconButton = styled(IconButton)({
  position: 'absolute',
  right: 0,
  top: '50%',
  transform: 'translateY(-50%)',
});

const InputContainer = styled(Stack)({
  position: 'relative',
  width: '100%',
});

const ErrorContainer = styled(Stack)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  paddingLeft: 0,
  paddingRight: theme.spacing(2),
}));

interface DynamicContactSearchFormProps {
  onSuccess?: (data: SearchData) => void;
  onError?: (error: Error) => void;
  searchType: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mutationFn: MutationFunction<any, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: ObjectSchema<any>;
  placeholder: string;
  inputName: string;
  dataCy: string;
  type?: string;
}

interface FormData {
  [key: string]: string;
}

export function DynamicContactSearchForm({
  onSuccess,
  onError,
  searchType,
  mutationFn,
  schema,
  placeholder,
  inputName,
  dataCy,
  type,
}: DynamicContactSearchFormProps) {
  const { session } = useSession();
  const { account } = session;
  const { logError, trackEvent } = useAppConfigContext();
  const {
    links: { baseUrl },
  } = useAppConstantsContext();

  const query = useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      onSuccess &&
        onSuccess({
          permalink: data.report.permalink,
          reportType: searchType,
          searchParams: `${inputName}=${encodeURIComponent(
            variables[inputName]
          )}`,
        });
      openReportInNewTab(
        {
          [inputName]: variables[inputName],
          searchType: searchType as SearchType,
        },
        baseUrl
      );
    },
    onError: (error: Error) => {
      logError(`${searchType} Search`, error);
      onError && onError(error);
    },
  });

  const onSubmit = (formData: FormData) => {
    if (!searchFormsRecyclingValidation(account)) return;
    trackEvent(`${searchType} search`, 'engagement click', 'Contact search');
    query.mutate(formData);
  };

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    control,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      [inputName]: '',
    },
  });

  const inputValue = useWatch({ control, name: inputName });

  return (
    <FormControl
      component="form"
      id={`search-${searchType}`}
      onSubmit={handleSubmit(onSubmit)}
      sx={{ width: '100%' }}
      data-testid={`search-${searchType}`}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <InputContainer>
          <Input
            {...register(inputName)}
            fullWidth
            id={inputName}
            name={inputName}
            type={type || 'text'}
            placeholder={placeholder}
            error={Boolean(errors[inputName])}
            value={inputValue}
            inputProps={{
              'data-cy': dataCy,
              endadornment: inputValue && (
                <InputAdornment position="end">
                  <ClearIconButton
                    type="button"
                    color="success"
                    onClick={() => reset({ [inputName]: '' })}
                  >
                    <Icons.Clear />
                  </ClearIconButton>
                </InputAdornment>
              ),
            }}
            data-testid={dataCy}
          />
        </InputContainer>
        <SquareIconButton
          type="submit"
          color="success"
          data-cy={`${searchType}_btn_header`}
          data-testid={`${searchType}_btn_header`}
        >
          <Icons.ArrowForward fontSize="small" />
        </SquareIconButton>
      </Stack>
      {errors[inputName] && (
        <ErrorContainer>
          <Text color="error">
            <Icons.Warning
              fontSize="small"
              sx={{ verticalAlign: 'bottom', mr: 0.5 }}
            />
            {errors[inputName]?.message}
          </Text>
        </ErrorContainer>
      )}
    </FormControl>
  );
}
